import React from "react"
import { Chip, type ChipProps, type Theme } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { resolveViraPalette } from "../theme/resolveViraPalette"

export type StatusChipVariant =
  | "virasafe"
  | "virapath"
  | "dbt"
  | "default"
  | "success"
  | "warning"
  | "info"

function variantSx(
  theme: Theme,
  tone: StatusChipVariant,
): { bg: string; color: string } {
  const vira = resolveViraPalette(theme)
  switch (tone) {
    case "virasafe":
    case "warning":
      return { bg: vira.chipViraSafeBg, color: vira.chipViraSafeText }
    case "virapath":
    case "success":
      return { bg: vira.chipViraPathBg, color: vira.chipViraPathText }
    case "dbt":
      return { bg: vira.chipDbtBg, color: vira.chipDbtText }
    case "default":
    case "info":
      return { bg: vira.lavender, color: vira.lavenderText }
  }
}

export type StatusChipProps = Omit<ChipProps, "variant" | "color"> & {
  tone?: StatusChipVariant
  /** When true, uses slightly larger padding for denser UI areas */
  dense?: boolean
}

export function StatusChip({
  tone = "default",
  dense = false,
  label,
  disabled,
  sx,
  ...rest
}: StatusChipProps) {
  return (
    <Chip
      size="small"
      label={label}
      disabled={disabled}
      sx={composeSx((theme) => {
        const palette = variantSx(theme, tone)
        return {
          height: dense ? 15 : 17,
          borderRadius: "5px",
          backgroundColor: palette.bg,
          color: palette.color,
          fontWeight: 600,
          fontSize: 9,
          letterSpacing: "0.81px",
          textTransform: "uppercase",
          opacity: disabled ? 0.5 : 1,
          "& .MuiChip-label": { px: dense ? 0.75 : 1, py: 0 },
        }
      }, sx)}
      {...rest}
    />
  )
}

/** Alias for StatusChip — tag-style chips (VIRASAFE, DBT, etc.). */
export const TagChip = StatusChip
export type TagChipProps = StatusChipProps
export type TagChipVariant = StatusChipVariant
